/**
 * IndexedDB 基盤(CLAUDE.md §5: localStorage は使わない)
 *
 * localStorage を避けているのは、同期APIで数十件の書き込みがメインスレッドを
 * 止めること、容量が小さいこと、iOS で予告なく消えやすいことによる。
 *
 * ストアはこの時点で全部作っておく。スタンプ保存(手順④)や同期キュー(手順⑥)で
 * バージョンを上げると、既にスタンプを持っている端末でマイグレーションが走る。
 * イベント当日にそれをやりたくないため、スキーマは §12 の定義どおり先に確定させる。
 */

const DB_NAME = "maitai";
const DB_VERSION = 1;

export const STORE = {
  /** 端末の設定・同意状態・device_id など */
  settings: "settings",
  /** 取得済みスタンプ。key = cp_id(同一CPは1回のみ / §8-4) */
  stamps: "stamps",
  /** サーバー送信待ちのログ。key = ulid(冪等キー / §4-4) */
  syncQueue: "sync_queue",
} as const;

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;

      if (!db.objectStoreNames.contains(STORE.settings)) {
        db.createObjectStore(STORE.settings);
      }
      if (!db.objectStoreNames.contains(STORE.stamps)) {
        const s = db.createObjectStore(STORE.stamps, { keyPath: "cp_id" });
        s.createIndex("timestamp", "timestamp");
      }
      if (!db.objectStoreNames.contains(STORE.syncQueue)) {
        const q = db.createObjectStore(STORE.syncQueue, { keyPath: "ulid" });
        q.createIndex("created_at", "created_at");
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () =>
      reject(new Error("IndexedDB がブロックされています(他のタブが開いている可能性)"));
  });

  return dbPromise;
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function get<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDb();
  const tx = db.transaction(store, "readonly");
  return promisify<T | undefined>(tx.objectStore(store).get(key) as IDBRequest<T | undefined>);
}

export async function put(store: string, value: unknown, key?: IDBValidKey): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, "readwrite");
  await promisify(tx.objectStore(store).put(value, key));
  await txDone(tx);
}

export async function getAll<T>(store: string): Promise<T[]> {
  const db = await openDb();
  const tx = db.transaction(store, "readonly");
  return promisify<T[]>(tx.objectStore(store).getAll() as IDBRequest<T[]>);
}

export async function del(store: string, key: IDBValidKey): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, "readwrite");
  await promisify(tx.objectStore(store).delete(key));
  await txDone(tx);
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}
